
type Role = "admin" | "head" | "scout" | "screener";
type AllowedRoles = ("*" | Role)[];

const RIGHTS_CONFIG = {
  PLAYERS_DB: {
    READ: ["*"],
  },
  LONGLIST: {
    READ: ["*"],
    WRITE: ["*"],
  },
  SHORTLIST: {
    READ: ["*"],
    WRITE: ["*"],
  },
  CLUB_DB: {
    READ: ["*"],
  },
  TASKS: {
    READ: ["*"],
    WRITE: ["admin", "head"],
  },
  REPORTS: {
    READ: ["admin", "head"],
    WRITE: ["admin", "head"],
  },
  CONTACTS: {
    READ: ["*"],
    WRITE: ["*"],
  },
  USER_MANAGEMENT: {
    READ: ["admin"],
    WRITE: ["admin"],
  },
    MATCHES: {
    READ: ["*"],
    WRITE: ["*"],
  },
  SETTINGS: {
    READ: ["admin", "head"],
    WRITE: ["admin", "head"],
  }
} as const;

// Type helpers to extract paths from the config
type PathsToStringProps<T> = T extends AllowedRoles 
  ? never 
  : T extends Record<string, any>
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends AllowedRoles
          ? K
          : T[K] extends Record<string, any>
          ? K | `${K}.${PathsToStringProps<T[K]>}`
          : never
        : never;
    }[keyof T]
  : never;

type RightsPath = PathsToStringProps<typeof RIGHTS_CONFIG>;

// Helper to get nested property type
type GetNestedType<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? GetNestedType<T[K], Rest>
    : never
  : P extends keyof T
  ? T[P]
  : never;

/**
 * Type-safe helper function to check if a role has a specific right
 * @param role - The role to check
 * @param rightPath - The path to the specific right (e.g., "PLAYERS_DB.READ")
 * @returns boolean indicating if the role has the right
 */
export function hasRight<T extends RightsPath>(
  role: string | undefined | Role,
  rightPath: T
): GetNestedType<typeof RIGHTS_CONFIG, T> extends AllowedRoles ? boolean : never {

    if(!role) return false as any;

  const pathParts = rightPath.split('.');
  let current: any = RIGHTS_CONFIG;
  
  // Navigate through the nested structure
  for (const part of pathParts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return false as any;
    }
  }
  
  // Check if current is an AllowedRoles array
  if (Array.isArray(current)) {
    return (current.includes("*") || current.includes(role)) as any;
  }
  
  return false as any;
}